// When a connection window opens, pull the current project's lists + latest drawing PDFs into the cache.
import axios from "axios";
import _ from "lodash";
import { isOnline } from "./network";
import { getAccessToken } from "modules/auth/token";
import { writeThroughResponse } from "./cache";
import { applyFetchedDataToStore } from "./hydrate";
import { prefetchLatestDrawingPdfs } from "./pdfCache";

const listUrlsForProject = (projectId) => [
  `/project/${projectId}/qa/paint_inspections.json`,
  `/project/${projectId}/qa/welding_inspections.json`,
  `/project/${projectId}/qa/action_items.json`,
  `/project/${projectId}/qa/site/site_installations.json`,
  `/project/${projectId}/qa/site/surveillances.json`,
  `/project/${projectId}/drawings.json`,
  "/store/bookout_items.json",
  "/store/overalls.json",
  "/store/requests.json",
];

const mobileFormRecordUrls = (store) => {
  const forms = store.getState().custom_forms || {};
  return _(forms)
    .filter((form) => form?.on_mobile && !form.archived)
    .take(10)
    .map((form) => `/custom_forms/${form.id}/records.json`)
    .value();
};

const PREFETCH_TTL_MS = 10 * 60 * 1000;

let inflight = null;
let lastPrefetchAt = 0;

export const prefetchCurrentProject = async (store, { force } = {}) => {
  if (!store || !isOnline() || !getAccessToken()) return;
  if (_.isEmpty(store.getState().current_user)) return;
  if (!force && lastPrefetchAt && Date.now() - lastPrefetchAt < PREFETCH_TTL_MS) return;
  if (inflight) return inflight;

  inflight = (async () => {
    const projectId = store.getState().current_user?.current_project_id;
    const urls = [...(projectId ? listUrlsForProject(projectId) : []), ...mobileFormRecordUrls(store)];

    for (const url of urls) {
      if (!isOnline()) break;
      try {
        const { data } = await axios.get(url, { skipOffline: true, timeout: 20000 });
        await writeThroughResponse("get", url, data);
        applyFetchedDataToStore(store, url, data);
        if (_.includes(url, "/drawings") && _.isArray(data)) {
          await prefetchLatestDrawingPdfs(data, { isOnline });
        }
      } catch (_e) {
        // skip this list — next window can retry
      }
    }
  })().finally(() => {
    lastPrefetchAt = Date.now();
    inflight = null;
  });

  return inflight;
};
